import React, { Component, Fragment } from 'react';
import { IAppProps } from './models/IAppProps';
import { IAppState } from './models/IAppState';
import { inject, observer } from 'mobx-react';
import Wrapper from './styledComponents/Wrapper';
import DebugInfoAction from '../shared/DebugInfoAction';
import NoDataPlaceholder from '@kurtosys/ksys-app-components/dist/components/base/NoDataPlaceholder';
import Disclaimer from '@kurtosys/ksys-app-components/dist/components/overview/Disclaimer';
import { InjectedStyledComponent } from '../shared/InjectedStyledComponent';
import Loading from '../shared/Loading';
import SkeletonLoader from '../shared/SkeletonLoader';
import Application from '@kurtosys/ksys-app-components/dist/components/base/Application';
import Modal from '@kurtosys/ksys-app-components/dist/components/base/Modal';
import Card from '@kurtosys/ksys-app-components/dist/components/base/Card';
import Translate from '@kurtosys/ksys-app-components/dist/components/base/Translate';
import VisuallyHidden from '@kurtosys/ksys-app-components/dist/components/base/VisuallyHidden';
import { SearchBar } from '../SearchBar/SearchBar';
import { Results } from '../Results/Results';
import { injectDialogPlaceholder } from '../../utils/dialogPlaceholder';

// AriaLiveMessages component renders an aria live message
export const AriaLiveMessages: React.FC<any> = observer(
	({ appId, values: { alertMessage } }) => {
		return (
			<VisuallyHidden>
				<div id={ `${ appId }-alert` } role={ 'alert' } aria-live="polite">
					<Translate>{ alertMessage }</Translate>
				</div>
			</VisuallyHidden>
		);
	},
);

@inject('appStore', 'queryStore')
@observer
export class App extends Component<IAppProps, IAppState> {
	static configurationKey: 'app' = 'app';
	static styleKey: 'app' = 'app';
	constructor(props: IAppProps) {
		super(props);
		if (props.appStore) {
			injectDialogPlaceholder(props.appStore.dialogPlaceholderId);
		}
	}

	render() {
		const { className, appStore, queryStore } = this.props;
		if (!appStore || !queryStore) {
			return null;
		}
		const { isBootstrapped, skeletonLoader, skeletonLoaders, dataContextStatus } = appStore;
		if (!isBootstrapped || dataContextStatus === 'PENDING') {
			const { embedLoadingElement } = this.props;

			if (skeletonLoader && !embedLoadingElement) {
				return <SkeletonLoader config={ skeletonLoader } variables={ skeletonLoaders && skeletonLoaders.variables } />;
			}
			return <Loading embedLoadingElement={ embedLoadingElement } />;
		}
		const {
			theme,
			show,
			showPlaceholder,
			noDataPlaceholder: noDataPlaceholderText,
			noDataPlaceholderDisclaimer,
			libraryComponentsConfiguration,
			appCardProps,
			getTranslateFunction,
			culture,
			assets,
			assetCacheOptions,
			assetRegisters,
			getAccessibilityTextFunction,
			toggleButtonEnabled,
			modalProps,
			alertMessage,
			applicationId,
		} = appStore;

		if (!show) {
			return null;
		}

		const noDataPlaceholder =
			(noDataPlaceholderDisclaimer && <Disclaimer { ...noDataPlaceholderDisclaimer } />) ||
			(noDataPlaceholderText && <NoDataPlaceholder>{ noDataPlaceholderText }</NoDataPlaceholder>);

		const translate = getTranslateFunction();
		const accessibilityText = getAccessibilityTextFunction();

		return (
			<Application
				theme={ theme }
				configuration={ libraryComponentsConfiguration }
				translate={ translate }
				culture={ culture }
				assets={ assets }
				assetCacheOptions={ assetCacheOptions }
				assetRegisters={ assetRegisters }
				accessibilityText={ accessibilityText }
				analyticsHelper={ appStore.analyticsHelper }
			>
				<DebugInfoAction appStore={ appStore } />
				<Wrapper className={ className }>
					{
						toggleButtonEnabled && (
							<Modal { ...modalProps } >
								<SearchBar />
								<Results />
								<AriaLiveMessages appId={ applicationId } values={ { alertMessage } } />
							</Modal>
						)
					}
					{
						!toggleButtonEnabled && (
							<Card { ...appCardProps }>
								{ showPlaceholder && noDataPlaceholder }
								{ !showPlaceholder && (
									<Fragment>
										<SearchBar />
										<Results />
										<AriaLiveMessages appId={ applicationId } values={ { alertMessage } } />
									</Fragment>
								) }
							</Card>
						)
					}
				</Wrapper>
			</Application >
		);
	}
}

export default InjectedStyledComponent(App, 'app');
